import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { KeyRound, Smartphone, SmartphoneNfc } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { issuePairingPin, resetMobileDevice } from '../../api/client';
import { formatDateTime, formatRelative } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';

/**
 * Paired phone of an account (mobile PWA, U11): one phone per user, one-time PIN shown once,
 * association reset by whoever manages the account.
 */
export function MobilePairing({ user, onChanged }: { user: api.User; onChanged: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [pin, setPin] = useState<api.PairingPin | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const device = user.mobile.device;
  const refresh = async () => {
    await onChanged();
    await qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  };
  const issue = useMutation({
    mutationFn: (reset: boolean) => issuePairingPin(user.id, reset),
    onSuccess: async (p) => {
      setPin(p);
      setConfirmReset(false);
      await refresh();
    },
  });
  const reset = useMutation({
    mutationFn: () => resetMobileDevice(user.id),
    onSuccess: async () => {
      setConfirmReset(false);
      await refresh();
    },
  });
  const appUrl = `${location.origin}/m/`;

  return (
    <fieldset className="fieldset">
      <legend>{t('users.mobile.title')}</legend>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {device ? (
          <div className="toolbar" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <SmartphoneNfc size={20} style={{ color: 'var(--color-online)', flex: '0 0 auto' }} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong>{device.label ?? t('users.mobile.phone')}</strong>
              <span className="muted">
                {t('users.mobile.pairedSince', { when: formatDateTime(device.enrolled_at) })}
              </span>
              <span className="muted">
                {t('users.mobile.lastSeen', {
                  when: device.last_seen_at ? formatRelative(device.last_seen_at) : t('common.never'),
                })}
              </span>
            </div>
          </div>
        ) : (
          <div className="toolbar" style={{ flexWrap: 'nowrap' }}>
            <Smartphone size={20} className="muted" />
            <span className="muted">
              {user.mobile.pin_expires_at
                ? t('users.mobile.pinPending', { when: formatDateTime(user.mobile.pin_expires_at) })
                : t('users.mobile.notPaired')}
            </span>
          </div>
        )}

        {pin && (
          <div className="pin-box" role="status">
            <span className="muted">{t('users.mobile.pinFor', { username: user.username })}</span>
            <span className="pin-value">
              {pin.pin.slice(0, 4)} {pin.pin.slice(4)}
            </span>
            <span className="muted">
              {t('users.mobile.pinExpires', { when: formatDateTime(pin.expires_at) })}
            </span>
            <span className="field-hint">{t('users.mobile.instructions', { url: appUrl })}</span>
          </div>
        )}

        {user.can_manage && user.status === 'active' && (
          <div className="toolbar">
            {!device ? (
              <button
                type="button"
                className="btn"
                disabled={issue.isPending}
                onClick={() => issue.mutate(false)}
              >
                <KeyRound size={15} />{' '}
                {user.mobile.pin_expires_at ? t('users.mobile.regenerate') : t('users.mobile.generate')}
              </button>
            ) : confirmReset ? (
              <div className="confirm-box" style={{ width: '100%' }}>
                <span>{t('users.mobile.resetConfirm')}</span>
                <div className="toolbar">
                  <button
                    type="button"
                    className="btn btn-danger-solid"
                    disabled={issue.isPending}
                    onClick={() => issue.mutate(true)}
                  >
                    {t('users.mobile.resetAndPin')}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={reset.isPending}
                    onClick={() => reset.mutate()}
                  >
                    {t('users.mobile.resetOnly')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setConfirmReset(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-danger"
                onClick={() => setConfirmReset(true)}
              >
                {t('users.mobile.reset')}
              </button>
            )}
          </div>
        )}
        <ErrorNote error={issue.error ?? reset.error} />
      </div>
    </fieldset>
  );
}
